import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';
import { Save, ArrowLeft, Loader2 } from 'lucide-react';
import Navigation from '../components/Navigation';

interface CustomReportPageProps {
  navigate: (to: string) => void;
  reportId?: string;
}

interface ReportFormat {
  id: string;
  name: string;
  settings: {
    companyLogo: boolean;
    companyName: boolean;
    reportType: boolean;
    preparedBy: boolean;
    generatedOn: boolean;
    projectSnapshot: boolean;
    projectHighlights: boolean;
    tasksSnapshot: boolean;
    tasksHighlights: boolean;
    teamProductivity: boolean;
    additionalInfo: boolean;
    keyHighlights: boolean;
    achievements: boolean;
    upcomingActions: boolean;
    meetingMinutes: boolean;
  };
}

export default function CustomReportPage({ navigate, reportId }: CustomReportPageProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [settings, setSettings] = useState({
    companyLogo: false,
    companyName: false,
    reportType: false,
    preparedBy: false,
    generatedOn: false,
    projectSnapshot: false,
    projectHighlights: false,
    tasksSnapshot: false,
    tasksHighlights: false,
    keyHighlights: false,
    achievements: false,
    upcomingActions: false,
    teamProductivity: false,
    additionalInfo: false,
    meetingMinutes: false
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (reportId) {
      setIsEditing(true);
      fetchReportFormat(reportId);
    }
  }, [reportId]);

  const fetchReportFormat = async (id: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('custom_report_formats')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        setName(data.name);
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching report format:', error);
      toast.error('Failed to load report format');
      navigate('/reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for the report format');
      return;
    }

    if (!Object.values(settings).some(value => value)) {
      toast.error('Please select at least one option');
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (isEditing && reportId) {
        // Update existing format
        const { error } = await supabase
          .from('custom_report_formats')
          .update({
            name: name.trim(),
            settings,
            updated_at: new Date().toISOString()
          })
          .eq('id', reportId);

        if (error) throw error;
        toast.success('Report format updated successfully');
      } else {
        // Create new format
        const { error } = await supabase
          .from('custom_report_formats')
          .insert([{
            name: name.trim(),
            settings,
            user_id: user.id
          }]);

        if (error) {
          if (error.message?.includes('Maximum limit')) {
            toast.error('Maximum limit of 5 custom report formats reached');
          } else {
            throw error;
          }
          return;
        }
        toast.success('Report format saved successfully');
      }
      
      navigate('/reports');
    } catch (error) {
      console.error('Error saving format:', error);
      toast.error('Failed to save report format');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation
          onSignOut={async () => await supabase.auth.signOut()}
          tasks={[]}
          currentView="all"
          onViewChange={() => {}}
          navigate={navigate}
        />
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/reports')}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isEditing ? 'Edit Report Format' : 'Custom Format'}
              </h1>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Format
                </>
              )}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
            <div className="space-y-6">
              <div>
                <label htmlFor="format-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Format Name
                </label>
                <input
                  type="text"
                  id="format-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter format name"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Select Report Components
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.companyLogo}
                      onChange={(e) => setSettings(s => ({ ...s, companyLogo: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Company Logo</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.companyName}
                      onChange={(e) => setSettings(s => ({ ...s, companyName: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Customer Brand/Company Name</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.reportType}
                      onChange={(e) => setSettings(s => ({ ...s, reportType: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Weekly or Monthly Report</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.preparedBy}
                      onChange={(e) => setSettings(s => ({ ...s, preparedBy: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Prepared By</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.generatedOn}
                      onChange={(e) => setSettings(s => ({ ...s, generatedOn: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Generated On</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.projectHighlights}
                      onChange={(e) => setSettings(s => ({ ...s, projectHighlights: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Project Highlights</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.keyHighlights}
                      onChange={(e) => setSettings(s => ({ ...s, keyHighlights: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Key Highlights</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.achievements}
                      onChange={(e) => setSettings(s => ({ ...s, achievements: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Achievements</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.tasksHighlights}
                      onChange={(e) => setSettings(s => ({ ...s, tasksHighlights: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Tasks Highlights</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.upcomingActions}
                      onChange={(e) => setSettings(s => ({ ...s, upcomingActions: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Upcoming Actions</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.additionalInfo}
                      onChange={(e) => setSettings(s => ({ ...s, additionalInfo: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Additional Information</span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.meetingMinutes}
                      onChange={(e) => setSettings(s => ({ ...s, meetingMinutes: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Meeting Minutes</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}